import { createApi } from '@reduxjs/toolkit/query/react';

import {
  MedicalDocumentsEndpoints as Endpoints,
  type CreateMedicalDocumentRequest,
  type CreateMedicalDocumentResponse,
  type GetBranchMedicalExtensionRequest,
  type GetBranchMedicalExtensionResponse,
  type GetMedicalDocumentByIdRequest,
  type GetMedicalDocumentByIdResponse,
  type GetMedicalDocumentByRecordIdRequest,
  type GetMedicalDocumentByRecordIdResponse,
  type GetMedicalDocumentsByClientRequest,
  type GetMedicalDocumentsByClientResponse,
  type GetMedicalDocumentsFilterDataRequest,
  type GetMedicalDocumentsFilterDataResponse,
  type GetMedicalDocumentXmlRequest,
  type GetMedicalDocumentXmlResponse,
  type SearchMedicalDocumentsRequest,
  type SearchMedicalDocumentsResponse,
  type SendToMedFlexRequest,
  type SendToMedFlexResponse,
  type SignMedicalDocumentRequest,
  type SignMedicalDocumentResponse,
  type SubmitMedicalDocumentRequest,
  type SubmitMedicalDocumentResponse,
  type UpdateMedicalCardRequest,
  type UpdateMedicalCardResponse,
  type UpdateMedicalDocumentStatusRequest,
  type UpdateMedicalDocumentStatusResponse,
  type UpdateMedicalDocumentsStatusesRequest,
  type UpdateMedicalDocumentsStatusesResponse,
  type UpsertBranchMedicalExtensionRequest,
  type UpsertBranchMedicalExtensionResponse,
} from '@app/api/MedicalDocuments/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { MEDICAL_DOCUMENTS_CHUNK } from '@app/constants';

export const medicalDocumentsApi = createApi({
  reducerPath: 'medicalDocumentsApi',
  baseQuery: createApiBaseQuery(MEDICAL_DOCUMENTS_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetMedicalDocumentByRecordId]: query<
      GetMedicalDocumentByRecordIdResponse,
      GetMedicalDocumentByRecordIdRequest
    >({
      query: ({ recordId, ...params }) => ({
        url: `GetByRecord/record/${recordId}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CreateMedicalDocument]: mutation<
      CreateMedicalDocumentResponse,
      CreateMedicalDocumentRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'Create',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetMedicalDocumentsFilterData]: query<
      GetMedicalDocumentsFilterDataResponse,
      GetMedicalDocumentsFilterDataRequest
    >({
      query: (params) => ({
        url: 'GetMedicalDocumentsFilterData',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateMedicalCard]: mutation<
      UpdateMedicalCardResponse,
      UpdateMedicalCardRequest
    >({
      query: ({ id, data, ...params }) => ({
        url: `Update/${id}`,
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.SearchMedicalDocuments]: query<
      SearchMedicalDocumentsResponse,
      SearchMedicalDocumentsRequest
    >({
      query: (params) => ({
        url: 'Search',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetMedicalDocumentsByClient]: query<
      GetMedicalDocumentsByClientResponse,
      GetMedicalDocumentsByClientRequest
    >({
      query: (params) => ({
        url: 'GetByClient',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpsertBranchMedicalExtension]: mutation<
      UpsertBranchMedicalExtensionResponse,
      UpsertBranchMedicalExtensionRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'UpsertBranchMedicalExtension',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetBranchMedicalExtension]: query<
      GetBranchMedicalExtensionResponse,
      GetBranchMedicalExtensionRequest
    >({
      query: ({ branchId, ...params }) => ({
        url: `GetBranchMedicalExtension/${branchId}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SendToMedFlex]: mutation<
      SendToMedFlexResponse,
      SendToMedFlexRequest
    >({
      query: (params) => ({
        url: 'SendDocument',
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetMedicalDocumentById]: query<
      GetMedicalDocumentByIdResponse,
      GetMedicalDocumentByIdRequest
    >({
      query: ({ id, ...params }) => ({
        url: `GetById/${id}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SubmitMedicalDocument]: mutation<
      SubmitMedicalDocumentResponse,
      SubmitMedicalDocumentRequest
    >({
      query: (params) => ({
        url: 'Submit',
        method: Methods.POST,
        params,
      }),
    }),
    // Документ (СЭМД) в Base64 для подписания плагином КриптоПро. Пришёл на
    // смену GetHash: серверное ГОСТ-хэширование требует лицензионного пакета
    // КриптоПро, поэтому хэширует и подписывает сам плагин на стороне клиента.
    [Endpoints.GetMedicalDocumentXml]: query<
      GetMedicalDocumentXmlResponse,
      GetMedicalDocumentXmlRequest
    >({
      query: (params) => ({
        url: 'GetDocumentXml',
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SignMedicalDocument]: mutation<
      SignMedicalDocumentResponse,
      SignMedicalDocumentRequest
    >({
      query: ({ data, ...params }) => ({
        url: 'Sign',
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateMedicalDocumentStatus]: mutation<
      UpdateMedicalDocumentStatusResponse,
      UpdateMedicalDocumentStatusRequest
    >({
      query: (params) => ({
        url: 'UpdateDocumentStatus',
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.UpdateMedicalDocumentsStatuses]: mutation<
      UpdateMedicalDocumentsStatusesResponse,
      UpdateMedicalDocumentsStatusesRequest
    >({
      query: (params) => ({
        url: 'UpdateDocumentsStatuses',
        method: Methods.POST,
        params,
      }),
    }),
  }),
});

export const {
  useLazyGetMedicalDocumentByRecordIdQuery,
  useCreateMedicalDocumentMutation,
  useGetMedicalDocumentsFilterDataQuery,
  useUpdateMedicalCardMutation,
  useLazySearchMedicalDocumentsQuery,
  useGetMedicalDocumentsByClientQuery,
  useUpsertBranchMedicalExtensionMutation,
  useGetBranchMedicalExtensionQuery,
  useSendToMedFlexMutation,
  useGetMedicalDocumentByIdQuery,
  useSubmitMedicalDocumentMutation,
  useLazyGetMedicalDocumentXmlQuery,
  useSignMedicalDocumentMutation,
  useUpdateMedicalDocumentStatusMutation,
  useUpdateMedicalDocumentsStatusesMutation,
} = medicalDocumentsApi;
